/**
 * Abilitazione massiva del diario di salute: una sola operazione per più
 * atleti o per interi gruppi, invece di aprire la scheda di un atleta per
 * volta (`WellnessCoachDialog`, che resta il posto dove si legge l'andamento
 * del singolo).
 *
 * La UI è in tre passi dichiarati — **chi**, **cosa fare**, **cosa monitorare** —
 * perché qui l'interruttore non è uno stato ma un'azione: scritto come switch
 * acceso di default sembrava dire «questo gruppo ha il diario attivo», mentre
 * dice «a chi selezioni, accendilo». Per la stessa ragione ogni atleta porta
 * accanto il suo stato reale (attivo / consenso da accettare / spento) e ogni
 * gruppo quanti dei suoi lo hanno già: senza, davanti a 97 atleti non c'è modo
 * di sapere su chi si sta per intervenire.
 *
 * Due cose che la UI deve dire chiaramente, perché sono il cuore del flusso:
 * il **consenso non si abilita in massa** (l'abilitazione fa comparire il
 * diario, accettare resta un gesto di ogni atleta), e riabilitando si
 * **sovrascrivono** i parametri già scelti per quegli atleti.
 * Spec: docs/wellness/00-analisi-e-piano.md
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAPI } from "../../lib/utils";
import { matchesCoachAthleteSearch } from "../../lib/coachUtils";
import { WellnessFieldPicker } from "./WellnessFieldPicker";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Loader2, HeartPulse, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";

const toggle = (list, value) =>
	list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const OFF_STATE = { enabled: false, consentRequired: true };

export function WellnessBulkDialog({
	open,
	onOpenChange,
	athletes = [],
	groups = [],
	initialGroupId = null,
	onApplied,
	// Lo stato del diario deve coprire gli atleti che il chiamante mostra: in
	// una maschera di gestione "tutti" sono quelli della piattaforma, altrimenti
	// il cono. Senza, gli atleti fuori dal cono comparirebbero come «spento».
	scope = "mine",
}) {
	const { language, t } = useLanguage();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [catalog, setCatalog] = useState([]);
	// Azione da applicare ai selezionati, non lo stato di qualcuno.
	const [enabled, setEnabled] = useState(true);
	const [reminderEnabled, setReminderEnabled] = useState(true);
	const [selectedFields, setSelectedFields] = useState([]);
	const [selectedGroupIds, setSelectedGroupIds] = useState([]);
	const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);
	const [search, setSearch] = useState("");
	const [stateFilter, setStateFilter] = useState("all");
	// Stato corrente del diario, atleta per atleta: è ciò che permette di
	// distinguere «chi è già attivo» da «chi sto selezionando adesso».
	const [statusByAthlete, setStatusByAthlete] = useState({});
	const [summary, setSummary] = useState(null);

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const [catalogData, overview] = await Promise.all([
				fetchAPI(`/wellness/catalog?language=${language}`),
				fetchAPI(
					`/wellness/configs/overview${scope === "all" ? "?scope=all" : ""}`,
				).catch(() => null),
			]);
			setCatalog(catalogData?.fields || []);
			setSelectedFields(catalogData?.default_fields || []);
			const map = {};
			(overview?.configs || []).forEach((config) => {
				map[config.athlete_id] = {
					enabled: !!config.enabled,
					consentRequired: !!config.consent_required,
				};
			});
			setStatusByAthlete(map);
		} catch {
			toast.error(t("wellnessLoadError"));
		} finally {
			setLoading(false);
		}
	}, [language, t, scope]);

	useEffect(() => {
		if (!open) return;
		setEnabled(true);
		setReminderEnabled(true);
		setSearch("");
		setStateFilter("all");
		setSummary(null);
		setSelectedAthleteIds([]);
		setSelectedGroupIds(initialGroupId ? [initialGroupId] : []);
		loadData();
	}, [open, initialGroupId, loadData]);

	const stateOf = useCallback(
		(athleteId) => statusByAthlete[athleteId] || OFF_STATE,
		[statusByAthlete],
	);

	// Due passaggi distinti: la ricerca decide quali atleti esistono per questa
	// vista, il filtro di stato quanti se ne mostrano — e i contatori dei filtri
	// devono contare sui primi, altrimenti cambiano appena si filtra.
	const searchedAthletes = useMemo(
		() => athletes.filter((athlete) => matchesCoachAthleteSearch(athlete, search)),
		[athletes, search],
	);

	const searchedCounts = useMemo(() => {
		const active = searchedAthletes.filter(
			(athlete) => stateOf(athlete.user_id).enabled,
		).length;
		return {
			all: searchedAthletes.length,
			active,
			inactive: searchedAthletes.length - active,
		};
	}, [searchedAthletes, stateOf]);

	const filteredAthletes = useMemo(() => {
		if (stateFilter === "all") return searchedAthletes;
		const wantActive = stateFilter === "active";
		return searchedAthletes.filter(
			(athlete) => stateOf(athlete.user_id).enabled === wantActive,
		);
	}, [searchedAthletes, stateFilter, stateOf]);

	// Chi riceverà davvero l'operazione: gli atleti spuntati più i membri dei
	// gruppi scelti, contati una volta sola (il backend fa la stessa unione e
	// ricontrolla comunque atleta per atleta).
	const recipientIds = useMemo(() => {
		const ids = new Set(selectedAthleteIds);
		groups
			.filter((group) => selectedGroupIds.includes(group.group_id))
			.forEach((group) => (group.athlete_ids || []).forEach((id) => ids.add(id)));
		return ids;
	}, [selectedAthleteIds, selectedGroupIds, groups]);

	const recipientBreakdown = useMemo(() => {
		let active = 0;
		recipientIds.forEach((id) => {
			if (stateOf(id).enabled) active += 1;
		});
		return { active, inactive: recipientIds.size - active };
	}, [recipientIds, stateOf]);

	const allFilteredSelected =
		filteredAthletes.length > 0 &&
		filteredAthletes.every((athlete) => selectedAthleteIds.includes(athlete.user_id));

	const toggleAllFiltered = () => {
		const filteredIds = filteredAthletes.map((athlete) => athlete.user_id);
		setSelectedAthleteIds((prev) =>
			allFilteredSelected
				? prev.filter((id) => !filteredIds.includes(id))
				: Array.from(new Set([...prev, ...filteredIds])),
		);
	};

	const handleApply = async () => {
		setSaving(true);
		setSummary(null);
		try {
			const result = await fetchAPI("/wellness/configs/bulk", {
				method: "POST",
				body: JSON.stringify({
					enabled,
					fields: selectedFields,
					reminder_enabled: reminderEnabled,
					athlete_ids: selectedAthleteIds,
					group_ids: selectedGroupIds,
				}),
			});
			setSummary(result);
			// Lo stato mostrato si aggiorna con quello che il backend ha davvero
			// scritto: dopo l'operazione i pallini nella lista sono il nuovo vero.
			if (result?.athletes?.length) {
				setStatusByAthlete((prev) => {
					const next = { ...prev };
					result.athletes.forEach((athlete) => {
						next[athlete.athlete_id] = {
							enabled: !!athlete.enabled,
							consentRequired: !!athlete.consent_required,
						};
					});
					return next;
				});
			}
			toast.success(
				t("wellnessBulkApplied").replace("{count}", result?.updated ?? 0),
			);
			onApplied?.(result);
		} catch (error) {
			toast.error(error?.message || t("wellnessSaveError"));
		} finally {
			setSaving(false);
		}
	};

	const canApply =
		!saving &&
		recipientIds.size > 0 &&
		(!enabled || selectedFields.length > 0);

	const stateChip = (athleteId) => {
		const state = stateOf(athleteId);
		if (!state.enabled) {
			return { label: t("wellnessInactive"), tone: "text-muted-foreground", dot: "bg-muted-foreground/60" };
		}
		if (state.consentRequired) {
			return { label: t("wellnessConsentToAccept"), tone: "text-amber-400", dot: "bg-amber-400" };
		}
		return { label: t("wellnessActive"), tone: "text-green-400", dot: "bg-green-400" };
	};

	const filterTabs = [
		{ key: "all", label: t("wellnessBulkFilterAll"), count: searchedCounts.all },
		{ key: "active", label: t("wellnessBulkFilterActive"), count: searchedCounts.active },
		{ key: "inactive", label: t("wellnessBulkFilterInactive"), count: searchedCounts.inactive },
	];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-2xl max-h-[85vh] overflow-hidden">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle className="font-heading flex items-center gap-2 pr-6">
						<HeartPulse className="w-4 h-4 text-volt" />
						{t("wellnessBulkTitle")}
					</DialogTitle>
				</DialogHeader>

				{loading ? (
					<div className="flex items-center justify-center py-10 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin mr-2" />
						{t("loading")}
					</div>
				) : (
					<>
						<div className="overflow-y-auto max-h-[58vh] pr-1 space-y-5">
							<p className="text-xs text-muted-foreground">
								{t("wellnessBulkDescription")}
							</p>

							{/* 1 · Chi ─────────────────────────────────────────── */}
							<section className="space-y-3">
								<p className="label-tactical">{t("wellnessBulkStepRecipients")}</p>

								{groups.length > 0 && (
									<div className="space-y-2">
										<p className="text-xs text-muted-foreground flex items-center gap-1.5">
											<Users className="w-3.5 h-3.5" />
											{t("groups")}
										</p>
										<div className="flex flex-wrap gap-2">
											{groups.map((group) => {
												const active = selectedGroupIds.includes(group.group_id);
												const memberIds = group.athlete_ids || [];
												const activeMembers = memberIds.filter(
													(id) => stateOf(id).enabled,
												).length;
												return (
													<button
														key={group.group_id}
														type="button"
														aria-pressed={active}
														onClick={() =>
															setSelectedGroupIds((prev) =>
																toggle(prev, group.group_id),
															)
														}
														className={`rounded-sm border px-3 py-1.5 text-left transition-colors ${
															active
																? "border-volt/60 bg-volt/10 text-volt"
																: "border-border bg-secondary hover:border-volt/40"
														}`}
													>
														<span className="block text-sm">{group.name}</span>
														<span className="block text-[10px] text-muted-foreground">
															{t("wellnessBulkGroupMembers").replace(
																"{count}",
																memberIds.length,
															)}
															{" · "}
															{activeMembers > 0
																? t("wellnessBulkGroupActive").replace(
																		"{count}",
																		activeMembers,
																	)
																: t("wellnessBulkGroupNoneActive")}
														</span>
													</button>
												);
											})}
										</div>
									</div>
								)}

								<div className="space-y-2">
									<div className="flex items-center justify-between gap-2">
										<p className="text-xs text-muted-foreground">{t("athletes")}</p>
										{filteredAthletes.length > 0 && (
											<button
												type="button"
												onClick={toggleAllFiltered}
												className="text-xs text-volt hover:underline"
											>
												{allFilteredSelected
													? t("wellnessBulkClearSelection")
													: t("wellnessBulkSelectAll")}
											</button>
										)}
									</div>

									{/* Filtro per stato: il modo diretto per rispondere a
									    «chi non ce l'ha ancora?» e selezionarli in blocco. */}
									<div className="flex flex-wrap gap-1.5">
										{filterTabs.map((tab) => (
											<button
												key={tab.key}
												type="button"
												aria-pressed={stateFilter === tab.key}
												onClick={() => setStateFilter(tab.key)}
												className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${
													stateFilter === tab.key
														? "border-volt/60 bg-volt/10 text-volt"
														: "border-border bg-secondary text-muted-foreground hover:border-volt/40"
												}`}
											>
												{tab.label}
												<span className="ml-1.5 opacity-70">{tab.count}</span>
											</button>
										))}
									</div>

									<div className="relative">
										<Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
										<Input
											value={search}
											onChange={(e) => setSearch(e.target.value)}
											placeholder={t("searchAthlete")}
											className="bg-secondary border-transparent rounded-sm pl-9"
										/>
									</div>

									<div className="max-h-56 overflow-y-auto space-y-1 pr-1">
										{filteredAthletes.map((athlete) => {
											const checked = selectedAthleteIds.includes(athlete.user_id);
											// Chi arriva da un gruppo selezionato lo decide il gruppo:
											// spuntarlo qui non cambierebbe i destinatari.
											const fromGroup =
												!checked && recipientIds.has(athlete.user_id);
											const chip = stateChip(athlete.user_id);
											return (
												<button
													key={athlete.user_id}
													type="button"
													aria-pressed={checked}
													disabled={fromGroup}
													title={
														fromGroup ? t("wellnessBulkFromGroupHint") : undefined
													}
													onClick={() =>
														setSelectedAthleteIds((prev) =>
															toggle(prev, athlete.user_id),
														)
													}
													className={`w-full text-left rounded-sm border px-3 py-2 transition-colors ${
														checked || fromGroup
															? "border-volt/60 bg-volt/10"
															: "border-border bg-secondary hover:border-volt/40"
													}`}
												>
													<span className="flex items-center justify-between gap-2">
														<span className="min-w-0">
															<span className="block text-sm font-medium truncate">
																{athlete.name}
															</span>
															{athlete.email && (
																<span className="block text-xs text-muted-foreground truncate">
																	{athlete.email}
																</span>
															)}
															<span
																className={`mt-1 flex items-center gap-1.5 text-[11px] ${chip.tone}`}
															>
																<span
																	aria-hidden="true"
																	className={`w-1.5 h-1.5 rounded-full shrink-0 ${chip.dot}`}
																/>
																{chip.label}
															</span>
														</span>
														{fromGroup ? (
															<Badge
																variant="outline"
																className="rounded-sm text-[10px] shrink-0"
															>
																{t("wellnessBulkFromGroup")}
															</Badge>
														) : (
															<span
																aria-hidden="true"
																className={`w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center text-[10px] ${
																	checked
																		? "bg-volt border-volt text-white"
																		: "border-border"
																}`}
															>
																{checked ? "✓" : ""}
															</span>
														)}
													</span>
												</button>
											);
										})}
										{filteredAthletes.length === 0 && (
											<p className="text-xs text-muted-foreground py-3 text-center">
												{t("noAthletesFound")}
											</p>
										)}
									</div>
								</div>
							</section>

							{/* 2 · Cosa fare ───────────────────────────────────── */}
							<section className="space-y-2">
								<p className="label-tactical">{t("wellnessBulkStepAction")}</p>
								<div className="grid grid-cols-2 gap-2">
									{[
										{ value: true, label: t("wellnessBulkActionEnable") },
										{ value: false, label: t("wellnessBulkActionDisable") },
									].map((action) => {
										const active = enabled === action.value;
										return (
											<button
												key={String(action.value)}
												type="button"
												aria-pressed={active}
												onClick={() => setEnabled(action.value)}
												className={`rounded-sm border px-3 py-2 text-sm transition-colors ${
													active
														? "border-volt bg-volt/10 text-volt font-medium"
														: "border-border bg-secondary text-muted-foreground hover:border-volt/40"
												}`}
											>
												{action.label}
											</button>
										);
									})}
								</div>
								<p className="text-xs text-muted-foreground">
									{enabled
										? t("wellnessEnabledDescription")
										: t("wellnessBulkDisableDescription")}
								</p>
							</section>

							{/* 3 · Cosa monitorare ─────────────────────────────── */}
							{enabled && (
								<section className="space-y-3">
									<p className="label-tactical">{t("wellnessBulkStepSettings")}</p>

									<div className="flex items-center justify-between gap-3 p-3 rounded-sm bg-secondary border border-border">
										<div className="min-w-0">
											<p className="text-sm font-medium">
												{t("wellnessDailyReminder")}
											</p>
											<p className="text-xs text-muted-foreground">
												{t("wellnessDailyReminderDescription")}
											</p>
										</div>
										<Switch
											checked={reminderEnabled}
											onCheckedChange={setReminderEnabled}
										/>
									</div>

									<WellnessFieldPicker
										catalog={catalog}
										selected={selectedFields}
										onToggle={(key) =>
											setSelectedFields((prev) => toggle(prev, key))
										}
									/>
									{selectedFields.length === 0 && (
										<p className="text-xs text-amber-400">
											{t("wellnessSelectAtLeastOne")}
										</p>
									)}
									<p className="text-xs text-muted-foreground">
										{t("wellnessBulkOverwriteWarning")}
									</p>
									<p className="text-xs text-amber-400">
										{t("wellnessBulkConsentNotice")}
									</p>
								</section>
							)}

							{summary && (
								<div className="rounded-sm border border-border bg-secondary p-3 space-y-1 text-xs">
									<p className="text-sm font-medium">
										{t("wellnessBulkApplied").replace(
											"{count}",
											summary.updated ?? 0,
										)}
									</p>
									{summary.skipped > 0 && (
										<p className="text-muted-foreground">
											{t("wellnessBulkSkipped").replace(
												"{count}",
												summary.skipped,
											)}
										</p>
									)}
									{summary.enabled && summary.pending_consent > 0 && (
										<p className="text-amber-400">
											{t("wellnessBulkPendingConsent").replace(
												"{count}",
												summary.pending_consent,
											)}
										</p>
									)}
								</div>
							)}
						</div>

						<div className="flex items-center justify-between gap-3 border-t border-border pt-3">
							<span className="text-xs text-muted-foreground min-w-0">
								{recipientIds.size === 0 ? (
									t("wellnessBulkRecipientsEmpty")
								) : (
									<>
										<span className="block text-foreground">
											{t("wellnessBulkRecipients").replace(
												"{count}",
												recipientIds.size,
											)}
										</span>
										<span className="block">
											{t("wellnessBulkRecipientsBreakdown")
												.replace("{active}", recipientBreakdown.active)
												.replace("{inactive}", recipientBreakdown.inactive)}
										</span>
									</>
								)}
							</span>
							<Button
								className="bg-volt hover:bg-volt/90 rounded-sm shrink-0"
								disabled={!canApply}
								onClick={handleApply}
							>
								{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
								{(enabled
									? t("wellnessBulkApplyEnableCount")
									: t("wellnessBulkApplyDisableCount")
								).replace("{count}", recipientIds.size)}
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
